import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import ShopTheLookServerOnly from './shop-the-look-server-only'

/* Hooks */
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {useIntl} from 'react-intl'

/* Components */
import RecommendationsCarousel from '../optimizely/assets/recommendationsCarousel'

import {sendClickEvent, setTempRecommendation} from '../../utils/gtm-utils'

/* Constants */
const RECOMMENDATION_TYPE = 'Product Detail Page - Cross Sell'

const sendGTMEvent = (title) => {
    setTempRecommendation({id: 'recommendations', name: title})
    const carouselName = (title || '').toLowerCase().trim().replace(/\s+/g, '_')
    sendClickEvent(`carousel - pdp - ${carouselName}`)
}

const ShopTheLook = ({
    product,
    // CMS configuration props
    CarouselTitle,
    ProductRecommendationCarouselBlockCarouselTitleStyle,
    ProductRecommendationCarouselBlockProductTitleStyle,
    ProductRecommendationCarouselBlockProductPriceStyle,
    TileNumber,
    TabletTileNumber,
    DesktopTileNumber,
    carouselID = 'shop-the-look',
    showAsGrid = false,
    RecommenderType = 'shop-the-look',
    serverRecommendations
}) => {
    const intl = useIntl()

    const variationGroupId = product?.variationGroups?.find((variationGroup) => {
        return variationGroup?.variationValues?.color === product?.variationValues?.color
    })?.productId

    const [productRecommendationsIds, setProductRecommendationsIds] = useState([])

    // Only fetch if variationGroupId is different from the current product
    // The main product already has recommendations, so no need to refetch the same product
    const needsVariationGroupFetch = variationGroupId && variationGroupId !== product?.id

    const {
        data: variationGroupProduct
    } = useProduct(
        {
            parameters: {
                id: variationGroupId,
                perPricebook: true,
                // Only fetch recommendations - we don't need availability, images, etc.
                // This also avoids the 60s cache reduction caused by availability
                expand: ['recommendations']
            }
        },
        {
            enabled: needsVariationGroupFetch,
            keepPreviousData: true
        }
    )

    // Use the fetched variation group product, or fall back to the main product's recommendations
    const productWithRecommendations = needsVariationGroupFetch ? variationGroupProduct : product

    const hasServerRecommendations = serverRecommendations != null
    const {data: fetchedRecommendations} = useProducts(
        {parameters: {
            ids: productRecommendationsIds,
            perPricebook: true,
            allImages: true,
            expand: ['promotions', 'variations', 'prices', 'images']
        }},
        {
            enabled: !hasServerRecommendations && Boolean(productRecommendationsIds?.length)
        }
    )
    const productRecommendations = hasServerRecommendations
        ? serverRecommendations
        : fetchedRecommendations

    const getShopTheLookRecommendations = (recommendations) => {
        return recommendations
            ?.filter(item => item?.recommendationType?.displayValue === RECOMMENDATION_TYPE)
            ?.map(item => item?.recommendedItemId)
    }

    useEffect(() => {
        if (productWithRecommendations) {
            setProductRecommendationsIds(getShopTheLookRecommendations(productWithRecommendations?.recommendations))
        }
    }, [productWithRecommendations])

    // Use CMS title exactly as received, only fallback if no CMS title provided
    const defaultTitle = intl.formatMessage({
        id: 'pdp_shop_the_look.title',
        defaultMessage: 'Shop The Look'
    })
    const effectiveTitle = CarouselTitle || defaultTitle

    // Prepare recommendation data for the carousel
    const recommendationData = {
        products: productRecommendations?.data || [],
        isLoading: false
    }

    const hasRecommendations =
        (productRecommendations?.total || 0) > 0 || productRecommendations?.data?.length > 0

    return (
        <Box width="100%" marginTop={{base: 6, lg: 0}}>
            <Box className="no-js-only">
                <ShopTheLookServerOnly
                    products={serverRecommendations?.data || []}
                    title={effectiveTitle}
                    titleStyle={ProductRecommendationCarouselBlockCarouselTitleStyle}
                    productTitleStyle={ProductRecommendationCarouselBlockProductTitleStyle}
                    productPriceStyle={ProductRecommendationCarouselBlockProductPriceStyle}
                />
            </Box>
            <Box className="js-only">
                {hasRecommendations && (
                    <RecommendationsCarousel
                        Title={effectiveTitle}
                        TitleStyle={ProductRecommendationCarouselBlockCarouselTitleStyle}
                        ProductTitleStyle={ProductRecommendationCarouselBlockProductTitleStyle}
                        ProductPriceStyle={ProductRecommendationCarouselBlockProductPriceStyle}
                        TileNumber={TileNumber || '2.25'}
                        TabletTileNumber={TabletTileNumber}
                        DesktopTileNumber={DesktopTileNumber || '2'}
                        carouselID={carouselID}
                        RecommenderType={RecommenderType}
                        _recommendationData={recommendationData}
                        recommendationData={recommendationData}
                        onProductClick={() => sendGTMEvent(effectiveTitle)}
                    />
                )}
            </Box>
        </Box>
    )
}

ShopTheLook.propTypes = {
    product: PropTypes.object.isRequired,
    // CMS configuration props
    CarouselTitle: PropTypes.string,
    ProductRecommendationCarouselBlockCarouselTitleStyle: PropTypes.object,
    ProductRecommendationCarouselBlockProductTitleStyle: PropTypes.object,
    ProductRecommendationCarouselBlockProductPriceStyle: PropTypes.object,
    TileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TabletTileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    DesktopTileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    carouselID: PropTypes.string,
    showAsGrid: PropTypes.bool,
    RecommenderType: PropTypes.string,
    serverRecommendations: PropTypes.shape({
        data: PropTypes.array,
        total: PropTypes.number
    })
}

export default ShopTheLook
